import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import ModalContext from 'contexts/ModalContext'
import TourV2DeparturesMonthsCalendar from './TourV2DeparturesMonthsCalendar'
import LayoutContainer from 'components/Common/LayoutContainer'
import TourV2DepartureTileList from './TourV2DepartureTileList'
import TourV2DepartureBookingModal from './TourV2DepartureBookingModal'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { areDeparturesSoldOut } from 'lib/tours/tourDepartureUtils'
import TourV2DeparturesSoldOutTile from './TourV2DeparturesSoldOutTile'
import TourV2YearNavigationBoxesMobile from './TourV2YearNavigationBoxesMobile'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { hasSelectedRooms } from 'lib/offer/offerPageUtil'

const smallCardLimit = 4

const TopNavigationWrapper = styled.div`
  margin-bottom: ${rem(12)};
`

const BottomNavigationWrapper = styled.div`
  margin-top: ${rem(12)};
`

interface Props {
  offer: App.Tours.TourV2Offer
  variation: App.Tours.TourV2OfferVariation
  departures: Array<App.Tours.TourV2OfferDeparture>
  travellers: Array<OfferPageRoom>
  packageType?: string
}

function TourV2DeparturesByYearList(props: Props) {
  const { offer, travellers, departures, variation, packageType } = props
  const intl = useIntl()

  const showModal = useContext(ModalContext)

  const lookingForEarlierDatesTitle = intl.formatMessage({
    id: '48G3YPH63UVuUVJlCz6f',
    defaultMessage: 'Looking for earlier dates?',
    description: 'Navigation title prompting user to view tour departures from a previous year',
  })
  const bookingForNextYearTitle = intl.formatMessage({
    id: 'KGHkyoZ-Z_cPptp4olJB',
    defaultMessage: 'Booking for next year?',
    description: 'Navigation title prompting user to view tour departures for the following year',
  })

  const onTileSelect = useCallback((departure: App.Tours.TourV2OfferDeparture) => {
    showModal(<TourV2DepartureBookingModal
      offer={offer}
      defaultDeparture={departure}
      defaultStep={hasSelectedRooms(travellers) ? 'departure' : 'travellers'}
      packageType={packageType}
    />)
  }, [offer, showModal, travellers, packageType])

  const onMonthSelect = useCallback((selectedDate: Date) => {
    showModal(<TourV2DepartureBookingModal
      offer={offer}
      defaultDate={selectedDate}
      defaultStep={hasSelectedRooms(travellers) ? 'departure' : 'travellers'}
      packageType={packageType}
    />)
  }, [offer, showModal, travellers, packageType])

  const onUpdateTravellers = useCallback(() => {
    showModal(<TourV2DepartureBookingModal
      offer={offer}
      defaultStep="travellers"
      packageType={packageType}
    />)
  }, [offer, showModal, packageType])

  const soldOutReason = useMemo(() => {
    return areDeparturesSoldOut(departures, travellers)
  }, [departures, travellers])

  if (soldOutReason) {
    return <TourV2DeparturesSoldOutTile
      reason={soldOutReason}
      onUpdateTravelers={onUpdateTravellers}
    />
  }

  if (variation.departures.length <= smallCardLimit) {
    return <LayoutContainer>
      {/* Show previous year first if it exists, otherwise show next year if it exists */}
      {(offer.previousYearTour || offer.nextYearTour) && (
        <TopNavigationWrapper>
          <TourV2YearNavigationBoxesMobile
            linkTourDetails={{
              ...(offer.previousYearTour || offer.nextYearTour)!,
              title: offer.previousYearTour ? lookingForEarlierDatesTitle : bookingForNextYearTitle,
            }}
          />
        </TopNavigationWrapper>
      )}
      <TourV2DepartureTileList
        departures={departures}
        onDepartureSelect={onTileSelect}
        offer={offer}
        travellers={travellers}
        variation={variation}
        packageType={packageType}
      />
    </LayoutContainer>
  }

  return <LayoutContainer>
    {offer.previousYearTour && (
      <TopNavigationWrapper>
        <TourV2YearNavigationBoxesMobile
          linkTourDetails={{
            ...offer.previousYearTour,
            title: lookingForEarlierDatesTitle,
          }}
        />
      </TopNavigationWrapper>
    )}
    <TourV2DeparturesMonthsCalendar
      onSelect={onMonthSelect}
      offer={offer}
      departures={departures}
      travellers={travellers}
    />
    {offer.nextYearTour && (
      <BottomNavigationWrapper>
        <TourV2YearNavigationBoxesMobile
          linkTourDetails={{
            ...offer.nextYearTour,
            title: bookingForNextYearTitle,
          }}
        />
      </BottomNavigationWrapper>
    )}
  </LayoutContainer>
}

type MappedProps = Pick<Props, 'travellers' | 'variation'>

export default withOfferPageState((state): MappedProps => ({
  travellers: state.rooms,
  variation: state.tourV2Variation!,
}), TourV2DeparturesByYearList)
